// #region imports
// --- Angular
import { Injectable } from '@angular/core';

// --- RxJs
import { Observable, BehaviorSubject, forkJoin } from 'rxjs';

// --- HS Code
import { AppService } from '../app.service';
import {
  SortDirections,
  SurveyModel,
  SurveySummaryModel,
  NotificationTypes
} from '../../../models';
import { ResponseBaseService } from './response-base.service';
import { ResultsBaseService } from '../results/results-base.service';
import { UserNotificationBaseService } from '../notifications/user-notification-base.service';
import { not } from '@angular/compiler/src/output/output_ast';

// #endregion

@Injectable({
  providedIn: 'root'
})
export abstract class SurveyBaseService {
  protected _selfSurveys$: BehaviorSubject<SurveySummaryModel[]>;
  get selfSurveys$(): Observable<SurveySummaryModel[]> {
    return this._selfSurveys$;
  }

  protected _selectedSelfSurvey$: BehaviorSubject<SurveySummaryModel>;
  get selectedSelfSurvey$(): Observable<SurveySummaryModel> {
    return this._selectedSelfSurvey$;
  }

  protected _activeSurvey$: BehaviorSubject<SurveyModel>;
  get activeSurvey$(): Observable<SurveyModel> {
    return this._activeSurvey$;
  }

  protected _isAdding$: BehaviorSubject<boolean>;
  get isAdding$(): Observable<boolean> {
    return this._isAdding$;
  }

  protected _hasReport$: BehaviorSubject<boolean>;
  get hasReport$(): Observable<boolean> {
    return this._hasReport$;
  }

  protected _surveyError$: BehaviorSubject<string>;
  get surveyError$(): Observable<string> {
    return this._surveyError$;
  }

  constructor(
    protected appService: AppService,
    protected responseService: ResponseBaseService,
    protected resultsService: ResultsBaseService,
    protected notificationService: UserNotificationBaseService
  ) {
    this._activeSurvey$ = new BehaviorSubject<SurveyModel>(null);
    this._selfSurveys$ = new BehaviorSubject<SurveySummaryModel[]>(null);
    this._selectedSelfSurvey$ = new BehaviorSubject<SurveySummaryModel>(null);
    this._isAdding$ = new BehaviorSubject<boolean>(false);
    this._hasReport$ = new BehaviorSubject<boolean>(false);
    this._surveyError$ = new BehaviorSubject<string>(null);

    this.responseService.activeResponse$.subscribe(response => {
      if (!response || !this._selectedSelfSurvey$.value) {
        return;
      }

      const selectedSurvey = this._selectedSelfSurvey$.value;
      if (selectedSurvey) {
        selectedSurvey.status = response.status;
        selectedSurvey.updated = new Date();
      }
      this._selectedSelfSurvey$.next(selectedSurvey);
      this._hasReport$.next(selectedSurvey.hasReport);
    });

    this.responseService.surveyResponseProgress$.subscribe(progress => {
      if (!progress) {
        return;
      }

      const selectedSurvey = this._selectedSelfSurvey$.value;
      if (selectedSurvey) {
        selectedSurvey.percentageParticipantComplete = progress;
        selectedSurvey.updated = new Date();
      }
      this._selectedSelfSurvey$.next(selectedSurvey);
      console.log('Has Report: ' + selectedSurvey.hasReport);
      this._hasReport$.next(selectedSurvey.hasReport);
    });
  }

  updateSurveyName(newName: string): Observable<boolean> {
    const summary = this._selectedSelfSurvey$.value;
    if (summary) {
      summary.name = newName;
      this._selectedSelfSurvey$.next(summary);

      let survey = this._activeSurvey$.value;
      if (survey) {
        survey.name = newName;
      } else {
        survey = {
          id: summary.id,
          moniker: summary.moniker,
          name: newName
        } as SurveyModel;
      }
      this._activeSurvey$.next(survey);

      const summaries = this._selfSurveys$.value;
      if (summaries && summaries.length) {
        const foundSummary = summaries.find(
          s => s.id === survey.id
        );
        if (foundSummary) {
          foundSummary.name = newName;
        }
        this._selfSurveys$.next(summaries);
      }

      return this.update(survey);
    }
  }

  abstract loadSummaries(
    pageSize: number,
    pageNumber: number,
    filter: string,
    sortItem: string,
    sortDirection: SortDirections
  ): void;
  abstract loadSurveyById(id: string): void;
  abstract post(model: SurveyModel): Observable<SurveyModel>;
  abstract update(model: SurveyModel): Observable<boolean>;

  selectSelfSurvey(surveyIdentifier: string): void {
    const summaries = this._selfSurveys$.value;
    if (summaries && summaries.length) {
      const summary = summaries.find(
        s => s.moniker === surveyIdentifier || s.id === surveyIdentifier
      );
      if (summary) {
        this._selectedSelfSurvey$.next(summary);
        //console.log('Has Report: ' + summary.hasReport);
        this._hasReport$.next(summary.hasReport);
        this.responseService.loadResponse(summary && summary.moniker);
        this.resultsService.loadSurveyResults(summary.moniker, false);
      }
    }
  }
}
